import { fromBase64, toBase64 } from '@change-corgi/core/base64';

/**
 * Decodes a base64url-encoded string (RFC 4648 §5) back to its original form.
 *
 * Base64url swaps the standard base64 alphabet's `+`/`/` for `-`/`_` and
 * strips `=` padding so the result is safe to use unescaped in URLs and
 * filenames. This restores the standard alphabet and padding before
 * delegating to the standard base64 decoder.
 *
 * @param input - A base64url-encoded string.
 * @returns The decoded string.
 */
export function fromBase64Url(input: string): string {
	const remainder = input.length % 4;
	const recodedString = input
		.replace(/-/g, '+') // Convert '-' to '+'
		.replace(/_/g, '/') // Convert '_' to '/'
		.padEnd(input.length + (remainder === 0 ? 0 : 4 - remainder), '=');

	return fromBase64(recodedString);
}

/**
 * Encodes a string as base64url (RFC 4648 §5).
 *
 * Produces standard base64 and then swaps `+`/`/` for `-`/`_` and drops
 * `=` padding, so the result is safe to use unescaped in URLs and
 * filenames.
 *
 * @param input - The string to encode.
 * @returns The base64url-encoded string.
 */
export function toBase64Url(input: string): string {
	return toBase64(input).replace(/[+/=]/g, (match) => {
		switch (match) {
			case '+':
				return '-'; // Convert '+' to '-'
			case '/':
				return '_'; // Convert '/' to '_'
			case '=':
				return ''; // Remove ending '='
			default:
				return match;
		}
	});
}
